import {Theme} from "@emotion/react";
import {useDownBreakpoint} from "@hooks/useDownBreakpoint";
import {Button, ButtonGroup, SxProps} from "@mui/material";
import {Stack} from "@mui/system";
import dayjs, {Dayjs} from "dayjs";
import {DatePicker} from "./DatePicker";
import {useState} from "react";

const DateRangePicker = ({
  label,
  from,
  to,
  onChangeFrom,
  onChangeTo,
  time,
  sx,
}: {
  label: {from: string; to: string};
  from: Dayjs | null;
  to: Dayjs | null;
  onChangeFrom: (value: Dayjs | null) => void;
  onChangeTo: (value: Dayjs | null) => void;
  time?: boolean;
  sx?: SxProps<Theme>;
}) => {
  const isDownBP = useDownBreakpoint("md");
  const [customRange, setCustomRange] = useState<boolean>(false);

  const handleStaticRange = (range: "6h" | "12h" | "24h") => {
    const now = dayjs();
    let from: Dayjs;
    switch (range) {
      case "6h":
        from = now.subtract(6, "hour");
        break;
      case "12h":
        from = now.subtract(12, "hour");
        break;
      case "24h":
        from = now.subtract(24, "hour");
        break;
    }
    onChangeFrom(from);
    onChangeTo(null);
  };

  const diff = from ? from.diff(dayjs(), "hour") : 0;

  return (
    <Stack sx={sx}>
      {!customRange && (
        <ButtonGroup>
          <Button
            disabled={diff === -6}
            onClick={() => handleStaticRange("6h")}>
            6h
          </Button>
          <Button
            disabled={diff === -12}
            onClick={() => handleStaticRange("12h")}>
            12h
          </Button>
          <Button
            disabled={diff === -24}
            onClick={() => handleStaticRange("24h")}>
            24h
          </Button>
          <Button
            sx={{
              minWidth: 40,
            }}
            onClick={() => setCustomRange(true)}>
            ...
          </Button>
        </ButtonGroup>
      )}
      {customRange && (
        <Stack
          direction={"row"}
          flex={1}
          maxWidth={isDownBP ? undefined : 480}
          overflow={"auto"}
          border={`1px solid gray`}
          borderRadius={3}
          sx={{
            "& .MuiButtonBase-root": {
              border: "none",
            },
          }}>
          <Button
            sx={{
              minWidth: 40,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }}
            variant="outlined"
            onClick={() => setCustomRange(false)}>
            ...
          </Button>
          <DatePicker
            time={time}
            label={label.from}
            views={["month", "day", "hours", "minutes"]}
            value={from ? dayjs(from) : null}
            max={to ? dayjs(to) : undefined}
            onChange={d => onChangeFrom(d)}
            format={"D/MM/YY - HH:mm"}
            sx={{
              "& .MuiPickersInputBase-root": {borderRadius: 0, height: "100%"},
              flex: 1,
            }}
          />
          <DatePicker
            time={time}
            label={label.to}
            views={["month", "day", "hours", "minutes"]}
            value={to ? dayjs(to) : null}
            min={from ? dayjs(from) : undefined}
            onChange={d => onChangeTo(d)}
            format={"D/MM/YY - HH:mm"}
            sx={{
              "& .MuiPickersInputBase-root": {
                borderRadius: 0,
                borderTopRightRadius: 12,
                borderBottomRightRadius: 12,
                height: "100%",
              },

              flex: 1,
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};

export {DateRangePicker};
